import { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Contact from 'observian-ui/lib/svg/icons/contact'
import Button from 'observian-ui/lib/button'
import Logo from 'observian-ui/lib/logo'
import { Form as FinalForm, Field } from 'react-final-form'
import omit from 'lodash/omit'

import { workspaceForm } from 'api/form'
import HubspotTracking from 'components/Hubspot/Tracking'
import { isEmail, isPhoneNumber, isRequired } from 'utils/validation'

import style from './contactForm.module.css'

const ContactUsForm = ({
  className,
  descriptionPlaceholder,
  headerTitle,
  showLogo,
  showContactInfo,
  buttonLabel,
  ctaIntent,
  isSuccessfulMessage,
  ctaIntentSuffix,
  isSuccessfulCallback,
}) => {
  const [isSuccessful, setIsSuccessful] = useState(false)

  const handleOnSubmit = async (event) => {
    const [firstName, lastName = ''] = event.fullName.split(' ')
    const content = undefined === event.description ? descriptionPlaceholder : event.description
    const ctaIntentValue =
      0 === ctaIntentSuffix.length ? event.ctaIntent : `${event.ctaIntent}-${ctaIntentSuffix}`

    const phone = undefined === event.phone ? '' : event.phone

    const validEvent = omit(event, 'fullName')

    const allFormData = {
      ...validEvent,
      firstName,
      lastName,
      content,
      subject: 'Workspace Support Request',
      phone,
      ctaIntent: ctaIntentValue,
      googleKeyword: localStorage.getItem('keyword') || '',
      keywordDate: localStorage.getItem('date') || '',
    }

    try {
      const res = await workspaceForm({ ...allFormData })

      if (200 === res.status) {
        return renderSubmission()
      }

      return console.error(res.data.message || 'Unknown Error.')
    } catch (error) {
      return console.error(error)
    }
  }

  const renderSubmission = (value = true) => {
    handleCallback(value)
    setIsSuccessful(value)
  }

  const handleOnDismiss = () => renderSubmission(false)

  const handleCallback = (value = false) => {
    if (isSuccessfulCallback) {
      return isSuccessfulCallback(value)
    }

    return null
  }

  return (
    <HubspotTracking>
      {({ hsContext }) => (
        <>
          {!isSuccessful ? (
            <FinalForm
              validateOnBlur
              onSubmit={(e) => handleOnSubmit({ ...e, hsContext })}
              initialValues={{ ctaIntent }}
            >
              {({ handleSubmit, pristine, submitting }) => (
                <form className={clsx(style.form, className)} onSubmit={handleSubmit}>
                  <header className={style.header}>
                    <div className={style.topbar}>
                      <svg width="300px" height="39px" viewBox="0 0 750 96">
                        <g fillRule="nonzero" stroke="none" strokeWidth={1} fill="none">
                          <g transform="translate(317 7)" fill="#5F6368">
                            <path d="M73.9 0.15L82.73 0.15 65.16 66.02 56.51 66.02 42.34 22.6 41.97 22.6 27.8 66.02 19.15 66.02 0.85 0.15 9.68 0.15 23.57 51.95 23.94 51.95 38.2 8.99 46.11 8.99 60.37 51.95 60.74 51.95z" />
                            <path d="M81.93 43.48c0-6.93 2.18-12.66 6.53-17.2 4.42-4.54 9.97-6.81 16.65-6.81 6.68 0 12.21 2.27 16.56 6.81 4.42 4.54 6.62 10.27 6.62 17.2 0 6.99-2.21 12.73-6.62 17.2-4.36 4.54-9.88 6.81-16.56 6.81-6.69 0-12.24-2.27-16.65-6.81-4.35-4.53-6.53-10.27-6.53-17.2zm8.46 0c0 4.85 1.41 8.77 4.23 11.78 2.82 3.01 6.32 4.51 10.49 4.51s7.67-1.5 10.49-4.51c2.82-3 4.23-6.93 4.23-11.78 0-4.78-1.41-8.68-4.23-11.68-2.88-3.07-6.38-4.6-10.49-4.6s-7.61 1.53-10.49 4.6c-2.82 3-4.23 6.9-4.23 11.68zM142.77 66.02h-8.46V20.94h8.1v7.36h.37c.86-2.39 2.62-4.43 5.29-6.12 2.67-1.69 5.29-2.53 7.87-2.53 2.45 0 4.54.37 6.26 1.1l-2.58 8.19c-1.04-.43-2.7-.64-4.97-.64-3.19 0-5.97 1.29-8.33 3.86-2.36 2.58-3.54 5.58-3.54 9.02v24.84h-.01z" />
                            <path d="M205.75 66.02L195.54 66.02 181.46 44.77 174.56 51.58 174.56 66.02 166.1 66.02 166.1 0.15 174.56 0.15 174.56 40.72 194.06 20.94 204.92 20.94 204.92 21.31 187.35 38.79 205.75 65.65z" />
                            <path d="M242.38 53.51c0 3.93-1.72 7.24-5.15 9.94-3.44 2.7-7.76 4.05-12.97 4.05-4.54 0-8.53-1.18-11.96-3.54-3.44-2.36-5.89-5.47-7.36-9.34l7.54-3.22c1.1 2.7 2.71 4.8 4.83 6.3s4.43 2.25 6.95 2.25c2.7 0 4.95-.58 6.76-1.75 1.81-1.16 2.71-2.54 2.71-4.14 0-2.88-2.21-5-6.62-6.35l-7.73-1.93c-8.77-2.21-13.16-6.44-13.16-12.7 0-4.11 1.67-7.41 5.01-9.89 3.34-2.48 7.62-3.73 12.83-3.73 3.99 0 7.59.95 10.81 2.85 3.22 1.9 5.47 4.45 6.76 7.64l-7.54 3.13c-.86-1.9-2.25-3.39-4.19-4.46-1.93-1.07-4.09-1.61-6.49-1.61-2.21 0-4.19.55-5.93 1.66-1.74 1.11-2.62 2.45-2.62 4.05 0 2.58 2.42 4.42 7.27 5.52l6.81 1.75c8.96 2.2 13.44 6.71 13.44 13.52zM271.77 67.49c-3.31 0-6.33-.71-9.06-2.12-2.73-1.41-4.83-3.28-6.3-5.61h-.37l.37 6.26v19.87h-8.46V20.94h8.1v6.26h.37c1.47-2.33 3.57-4.2 6.3-5.61 2.73-1.41 5.75-2.12 9.06-2.12 5.95 0 10.98 2.33 15.09 6.99 4.23 4.72 6.35 10.4 6.35 17.02 0 6.69-2.12 12.36-6.35 17.02-4.12 4.66-9.15 6.99-15.1 6.99zm-1.38-7.72c4.05 0 7.45-1.53 10.21-4.6 2.76-3 4.14-6.9 4.14-11.68 0-4.72-1.38-8.62-4.14-11.68-2.76-3.07-6.16-4.6-10.21-4.6-4.11 0-7.54 1.53-10.3 4.6-2.7 3.07-4.05 6.96-4.05 11.68 0 4.78 1.35 8.71 4.05 11.78 2.75 2.99 6.19 4.5 10.3 4.5zM316.39 19.47c6.26 0 11.19 1.67 14.81 5.01 3.62 3.34 5.43 7.93 5.43 13.75v27.78h-8.1v-6.26h-.37c-3.5 5.15-8.16 7.73-13.98 7.73-4.97 0-9.12-1.47-12.47-4.42-3.34-2.94-5.01-6.62-5.01-11.04 0-4.66 1.76-8.37 5.29-11.13 3.53-2.76 8.23-4.14 14.12-4.14 5.03 0 9.17.92 12.42 2.76v-1.93c0-2.94-1.17-5.44-3.5-7.5-2.33-2.05-5.06-3.08-8.19-3.08-4.72 0-8.46 1.99-11.22 5.98l-7.45-4.69c4.11-5.88 10.18-8.82 18.22-8.82zm-10.95 32.75c0 2.21.93 4.05 2.81 5.52 1.87 1.47 4.06 2.21 6.58 2.21 3.56 0 6.73-1.32 9.52-3.96 2.79-2.64 4.19-5.73 4.19-9.29-2.64-2.08-6.32-3.13-11.04-3.13-3.44 0-6.3.83-8.6 2.48-2.31 1.66-3.46 3.72-3.46 6.17zM365.32 67.49c-6.69 0-12.24-2.27-16.65-6.81-4.36-4.66-6.53-10.4-6.53-17.2 0-6.93 2.18-12.66 6.53-17.2 4.42-4.54 9.97-6.81 16.65-6.81 4.6 0 8.62 1.15 12.05 3.45 3.43 2.3 6.01 5.47 7.73 9.52l-7.73 3.22c-2.39-5.64-6.59-8.46-12.6-8.46-3.86 0-7.21 1.56-10.03 4.69-2.76 3.13-4.14 6.99-4.14 11.59s1.38 8.46 4.14 11.59c2.82 3.13 6.16 4.69 10.03 4.69 6.19 0 10.55-2.82 13.06-8.46l7.54 3.22c-1.66 4.05-4.25 7.22-7.77 9.52-3.52 2.3-7.62 3.45-12.28 3.45zM410.87 67.49c-6.62 0-12.08-2.27-16.38-6.81-4.29-4.54-6.44-10.27-6.44-17.2 0-6.87 2.08-12.59 6.26-17.16 4.17-4.57 9.51-6.85 16.01-6.85 6.68 0 12.01 2.16 15.96 6.49 3.96 4.32 5.93 10.38 5.93 18.17l-.09.92H396.7c.12 4.42 1.59 7.97 4.42 10.67 2.82 2.7 6.19 4.05 10.12 4.05 5.4 0 9.63-2.7 12.7-8.1l7.54 3.68c-2.02 3.8-4.83 6.78-8.42 8.92-3.59 2.14-7.65 3.22-12.19 3.22zm-13.52-29.44h25.85c-.25-3.13-1.52-5.72-3.82-7.77s-5.38-3.08-9.25-3.08c-3.19 0-5.93.98-8.23 2.94-2.3 1.97-3.82 4.6-4.55 7.91z" />
                          </g>
                          <path
                            d="M37.76 74.48C17.24 74.48 0 57.76 0 37.24 0 16.72 17.24 0 37.76 0c11.35 0 19.42 4.45 25.5 10.26l-7.17 7.17c-4.36-4.09-10.26-7.27-18.33-7.27-14.97 0-26.68 12.08-26.68 27.07s11.71 27.06 26.68 27.06c9.71 0 15.25-3.9 18.79-7.45 2.9-2.91 4.81-7.08 5.54-12.81h-24.6V33.88h34.48c.36 1.82.54 4 .54 6.36 0 7.63-2.09 17.08-8.8 23.8-6.53 6.8-14.88 10.44-25.95 10.44z"
                            fill="#4285F4"
                          />
                          <path
                            d="M124.16 50.5c0 13.81-10.79 23.98-24.02 23.98S76.11 64.3 76.11 50.5c0-13.9 10.79-23.98 24.02-23.98s24.03 10.08 24.03 23.98zm-10.52 0c0-8.63-6.26-14.53-13.51-14.53s-13.51 5.9-13.51 14.53c0 8.54 6.26 14.53 13.51 14.53 7.26 0 13.51-6 13.51-14.53z"
                            fill="#EA4335"
                          />
                          <path
                            d="M176.45 50.5c0 13.81-10.79 23.98-24.02 23.98S128.4 64.3 128.4 50.5c0-13.9 10.79-23.98 24.02-23.98s24.03 10.08 24.03 23.98zm-10.52 0c0-8.63-6.26-14.53-13.51-14.53s-13.51 5.9-13.51 14.53c0 8.54 6.26 14.53 13.51 14.53s13.51-6 13.51-14.53z"
                            fill="#FBBC04"
                          />
                          <path
                            d="M226.47 27.97v43.05c0 17.71-10.44 24.98-22.78 24.98-11.62 0-18.61-7.81-21.24-14.17l9.17-3.81c1.63 3.91 5.63 8.54 12.07 8.54 7.9 0 12.8-4.9 12.8-14.08v-3.45h-.36c-2.36 2.91-6.9 5.45-12.62 5.45-11.98 0-22.96-10.44-22.96-23.89 0-13.53 10.98-24.07 22.96-24.07 5.72 0 10.26 2.54 12.62 5.36h.36v-3.91h9.98zm-9.26 22.62c0-8.45-5.63-14.62-12.8-14.62-7.26 0-13.34 6.18-13.34 14.62 0 8.36 6.08 14.44 13.34 14.44 7.17 0 12.8-6.09 12.8-14.44z"
                            fill="#4285F4"
                          />
                          <path
                            fill="#34A853"
                            d="M244.57 2.54L244.57 73.02 234.04 73.02 234.04 2.54z"
                          />
                          <path
                            d="M285.38 58.4l8.17 5.45c-2.63 3.91-8.99 10.63-19.97 10.63-13.61 0-23.78-10.54-23.78-23.98 0-14.26 10.26-23.98 22.6-23.98 12.43 0 18.51 9.9 20.51 15.26L294 44.5l-32.04 13.26c2.45 4.81 6.26 7.27 11.62 7.27 5.35 0 9.08-2.63 11.8-6.63zm-25.14-8.63l21.42-8.9c-1.18-3-4.72-5.09-8.89-5.09-5.36 0-12.8 4.73-12.53 13.99z"
                            fill="#EA4335"
                          />
                        </g>
                      </svg>
                      <h3>{headerTitle}</h3>
                    </div>
                    {showLogo ? (
                      <Logo colorLogo className={style.logo} data-testid="form-logo" />
                    ) : null}
                  </header>
                  <div className={style.contactForm}>
                    <Field name="fullName" placeholder="Name" validate={isRequired}>
                      {({ input, meta, placeholder }) => (
                        <div className={style.inputContainer}>
                          <Contact type="person" className={style.inputIcon} />
                          <input {...input} placeholder={placeholder} id={input.name} />
                          {meta.error && meta.touched && (
                            <span
                              data-testid="form-fullName-error"
                              className={style.validationError}
                            >
                              {meta.error}
                            </span>
                          )}
                        </div>
                      )}
                    </Field>
                    <Field name="email" placeholder="email@example.com" validate={isEmail}>
                      {({ input, meta, placeholder }) => (
                        <div className={style.inputContainer}>
                          <Contact type="email-alt" className={style.inputIcon} />
                          <input
                            {...input}
                            placeholder={placeholder}
                            id={input.name}
                            type="email"
                          />
                          {meta.error && meta.touched && (
                            <span data-testid="form-email-error" className={style.validationError}>
                              {meta.error}
                            </span>
                          )}
                        </div>
                      )}
                    </Field>
                    <Field name="phone" placeholder="Phone Number" validate={isPhoneNumber}>
                      {({ input, meta, placeholder }) => (
                        <div className={clsx(style.inputContainer)}>
                          <Contact
                            type="phone"
                            className={clsx(style.inputIcon, style.phoneNumber)}
                          />
                          <input {...input} placeholder={placeholder} id={input.name} type="tel" />
                          {meta.error && meta.dirty && meta.touched && (
                            <span
                              data-testid="form-phoneNumber-error"
                              className={style.validationError}
                            >
                              {meta.error}
                            </span>
                          )}
                        </div>
                      )}
                    </Field>
                    <Field name="description" placeholder={descriptionPlaceholder}>
                      {({ input, placeholder }) => (
                        <div className={style.inputContainer}>
                          <Contact
                            type="message"
                            className={clsx(style.inputIcon, style.multiLineIcon)}
                          />
                          <textarea {...input} rows="5" placeholder={placeholder} id={input.name} />
                        </div>
                      )}
                    </Field>
                    <Field name="ctaIntent" type="hidden">
                      {({ input }) => <input {...input} type="hidden" />}
                    </Field>
                  </div>
                  <div className={clsx(style.actions, showContactInfo && style.showContactInfo)}>
                    {showContactInfo ? (
                      <>
                        <div data-testid="form-contact-info" className={style.contactInfo}>
                          <a rel="noopener noreferrer" href="mailto:info@observian.com">
                            info@observian.com
                          </a>
                          <a
                            className={style.phone}
                            rel="noopener noreferrer"
                            href="tel:801-895-3219"
                          >
                            801-895-3219
                          </a>
                          <a
                            className={style.address}
                            rel="noopener noreferrer"
                            target="_blank"
                            href="https://www.google.com/maps/place/Observian,+Inc./@40.7400222,-111.9863894,17z/data=!3m1!4b1!4m5!3m4!1s0x8752f3110eb2c4db:0x744729f4723d085e!8m2!3d40.7400182!4d-111.9842007"
                          >
                            1338 South Gustin Road
                            <br />
                            Salt Lake City, UT 84104
                          </a>
                        </div>
                        <div className={style.hrBlock} />
                      </>
                    ) : null}
                    <Button
                      type="primary"
                      label={buttonLabel}
                      className={clsx(
                        style.buttonCTA,
                        showContactInfo && style.showContactInfoButton
                      )}
                      disabled={pristine || submitting}
                    />
                  </div>
                </form>
              )}
            </FinalForm>
          ) : (
            <div className={style.success}>
              <span data-testid="form-success-message">{isSuccessfulMessage}</span>
              <Button
                type="cancel"
                label="Done"
                onClick={() => handleOnDismiss()}
                className={style.successButtonCTA}
              />
            </div>
          )}
        </>
      )}
    </HubspotTracking>
  )
}

ContactUsForm.defaultProps = {
  className: '',
  descriptionPlaceholder: 'Support ticket description',
  headerTitle: 'Submit Support Ticket',
  showLogo: false,
  showContactInfo: false,
  buttonLabel: 'Submit Ticket',
  isSuccessfulMessage: "Your ticket has been submitted, we'll be in touch soon!",
  ctaIntent: 'workspace-support-form',
  ctaIntentSuffix: '',
  isSuccessfulCallback: () => {},
}

ContactUsForm.propTypes = {
  className: PropTypes.string,
  descriptionPlaceholder: PropTypes.string,
  headerTitle: PropTypes.string,
  showLogo: PropTypes.bool,
  showContactInfo: PropTypes.bool,
  buttonLabel: PropTypes.string,
  isSuccessfulMessage: PropTypes.string,
  ctaIntent: PropTypes.string,
  ctaIntentSuffix: PropTypes.string,
  isSuccessfulCallback: PropTypes.func,
}

export default ContactUsForm
